"use client";

import { useEffect, useRef, useState, type FormEvent } from "react";
import Link from "next/link";
import { CalendlyBooking } from "@/components/calendly-booking";
import { LeadConversion } from "@/components/tracking/lead-conversion";
import { rememberedCampaignAttribution } from "@/lib/campaign-attribution";
import { submitLead } from "@/lib/submit-lead";

export function ConsultationModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const submitting = useRef(false);
  const title = useRef<HTMLHeadingElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [bookingError, setBookingError] = useState(false);
  const [bookingUrl, setBookingUrl] = useState("");
  const [bookingAttempt, setBookingAttempt] = useState(0);
  const [lead, setLead] = useState<{ reference: string; prefill: { name: string; email: string } } | null>(null);

  useEffect(() => {
    if (!open) { dialog.current?.close(); return; }
    dialog.current?.showModal();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previousOverflow; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    fetch("/api/booking?placement=modal", { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error("Booking settings unavailable");
        const data = await response.json() as { url: string };
        const url = new URL(data.url);
        if (url.protocol !== "https:" || url.hostname !== "calendly.com") throw new Error("Invalid booking URL");
        setBookingUrl(url.toString());
        setBookingError(false);
      })
      .catch(() => { if (!controller.signal.aborted) setBookingError(true); });
    return () => controller.abort();
  }, [open, bookingAttempt]);

  useEffect(() => { if (lead) title.current?.focus(); }, [lead]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting.current) return;
    const data = new FormData(event.currentTarget);
    if (data.get("company_website")) return;
    const name = String(data.get("name") ?? "").trim();
    const email = String(data.get("email") ?? "").trim();
    const website = String(data.get("website") ?? "").trim();
    try {
      const url = new URL(/^https?:\/\//i.test(website) ? website : `https://${website}`);
      if (!url.hostname.includes(".") || !["https:", "http:"].includes(url.protocol)) throw new Error();
      if (!name) throw new Error();
    } catch {
      setError("Please enter your full name and a valid website, such as example.com.");
      return;
    }
    submitting.current = true;
    setBusy(true);
    setError("");
    try {
      const result = await submitLead({ name, email, website, form_type: "consultation", topic: "Free consultation", message: "Request for a free consultation via the header booking modal.", form_page: window.location.pathname, ...rememberedCampaignAttribution(window.location.href) }, "book-free-consultation");
      if (result.reference === "DROPPED") throw new Error("Submission could not be confirmed");
      setLead({ reference: result.reference, prefill: { name, email } });
      dialog.current?.scrollTo(0, 0);
    } catch {
      setError("We could not save your request. Please try again or email contact@databidmachine.com.");
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }

  return (
    <dialog ref={dialog} className={`consultation-dialog${lead ? " consultation-dialog-booking" : ""}`} aria-labelledby="consultation-title" onClose={onClose} onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="consultation-content">
        <button className="consultation-close" type="button" aria-label="Close consultation" onClick={onClose}>×</button>
        <p className="consultation-step">Step {lead ? "2" : "1"} of 2 · {lead ? "Choose a time" : "Your details"}</p>
        <h2 ref={title} id="consultation-title" tabIndex={-1}>{lead ? "Thank you! Let’s find a time." : "Book a free consultation"}</h2>
        {lead ? (
          <>
            <p className="consultation-intro">Your request is saved. Choose a time that works for you below.</p>
            <LeadConversion source="consultation" reference={lead.reference} />
            {open && bookingUrl ? <CalendlyBooking key={bookingUrl} url={bookingUrl} source="consultation" reference={lead.reference} prefill={lead.prefill} compact /> : bookingError ? <div role="alert"><p>Your request is saved, but the calendar could not load.</p><button type="button" className="btn btn-ghost" onClick={() => { setBookingError(false); setBookingAttempt((value) => value + 1); }}>Retry calendar</button></div> : <p role="status">Loading available times…</p>}
          </>
        ) : (
          <>
            <p className="consultation-intro">Tell us a little about yourself, then choose a time to talk with our team.</p>
            <form onSubmit={onSubmit} className="consultation-form" data-hs-do-not-collect="true">
              <label htmlFor="consultation-name">Full name<input id="consultation-name" name="name" autoComplete="name" required maxLength={150} autoFocus /></label>
              <label htmlFor="consultation-email">Email<input id="consultation-email" name="email" type="email" autoComplete="email" required maxLength={254} /></label>
              <label htmlFor="consultation-website">Website<input id="consultation-website" name="website" type="text" inputMode="url" autoComplete="url" placeholder="example.com" required maxLength={500} /></label>
              <div className="sr-only" aria-hidden="true"><label>Leave this empty<input name="company_website" tabIndex={-1} autoComplete="off" /></label></div>
              {error ? <p className="consultation-error" role="alert">{error}</p> : null}
              <button type="submit" className="btn btn-accent btn-full" disabled={busy}>{busy ? "Saving your details…" : "Continue to Booking →"}</button>
              <p className="consultation-note">Choose a time in the next step. No obligation.</p>
              <p className="consultation-note">We’ll use your details to respond and prefill your Calendly booking. <Link href="/privacy-policy">Privacy policy</Link> · <a href="https://calendly.com/privacy" target="_blank" rel="noopener noreferrer">Calendly privacy</a></p>
            </form>
          </>
        )}
      </div>
    </dialog>
  );
}
